import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { ReactComponent as CheckCircleIcon } from '@/assets/icons/check-circle.svg';
import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import { ReactComponent as StopCircleIcon } from '@/assets/icons/stop-circle.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap, Td } from '@/components/Table';
import useAlerts from '@/hooks/useAlerts';
import { Dot } from '@/pages/OptimisationProject/components/OptimisationStatusBadge';
import { UserResourceV2 } from '@/types/resources';
import { api } from '@/utils/api';
import { RootState } from '@/utils/store';

import UserModal from './UserModal';

const Tr = styled.tr<{ $disabled?: boolean }>`
  td {
    ${({ $disabled, theme }) =>
      $disabled && `background: ${theme.colors.grayscaleSuperLight};`}
  }
`;

interface Props {
  user: UserResourceV2;
}

const dotColors = {
  'Team Member': 'green',
  'Team Lead': 'blue',
  Admin: 'yellow',
};

const UsersTableRow: FC<Props> = ({ user }) => {
  const [showEdit, setShowEdit] = useState(false);

  const currentUserId = useSelector(
    (rootState: RootState) => rootState.auth.user!.id,
  );

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const dotColor = (dotColors[user.role.name as keyof typeof dotColors] ||
    'green') as 'green' | 'yellow' | 'blue';

  const isDisabled = !!user.disabled_at;

  const onToggleDisabled = async () => {
    try {
      const confirmed = await alerts.confirm(
        isDisabled ? 'Enable this user?' : 'Disable this user?',
      );

      if (!confirmed) {
        return;
      }

      const action = isDisabled ? 'enable' : 'disable';

      await api.post(`/v2/users/${user.id}/${action}`);

      await queryClient.invalidateQueries(['users_v2']);

      alerts.close();
    } catch {
      alerts.error();
    }
  };

  return (
    <Tr $disabled={isDisabled}>
      <Td $type="smallBold">{user.name}</Td>
      <Td>{user.email}</Td>
      <Td>
        <Dot $color={dotColor} $withMargin />
        {user.role.name}
      </Td>
      <Td>
        {user.teams.map((team) => (
          <div key={team.id}>{team.name}</div>
        ))}
      </Td>
      <Td>
        {!!user.companies?.length
          ? user.companies?.map((company) => (
              <div key={company.id}>{company.name}</div>
            ))
          : '-'}
      </Td>
      <Td>
        {!!user.departments?.length
          ? user.departments.map((department) => (
              <div key={department.id}>{department.name}</div>
            ))
          : '-'}
      </Td>
      <Td $actionsCol>
        {currentUserId !== user.id && (
          <ActionsWrap>
            <ActionsDropdown>
              <ActionsDropdownButton
                as="button"
                onClick={() => setShowEdit(true)}
              >
                <span>
                  <PencilIcon />
                </span>
                Edit
              </ActionsDropdownButton>
              <ActionsDropdownButton as="button" onClick={onToggleDisabled}>
                <span>
                  {isDisabled ? <CheckCircleIcon /> : <StopCircleIcon />}
                </span>
                {isDisabled ? 'Enable' : 'Disable'}
              </ActionsDropdownButton>
            </ActionsDropdown>
          </ActionsWrap>
        )}
        <AnimatePresence>
          {showEdit && (
            <UserModal user={user} onClose={() => setShowEdit(false)} />
          )}
        </AnimatePresence>
      </Td>
    </Tr>
  );
};

export default UsersTableRow;
